import React, { useState, useEffect, useCallback, useRef } from 'react';
import { verifyPayment } from '../api/verify-payment';
import { toast } from 'react-hot-toast';
import { CheckCircle, XCircle, Clock, RefreshCw, AlertCircle, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useVerification } from '../contexts/VerificationContext';
import { PaykuValidator } from '../services/paykuValidator';

interface PaymentVerifierProps {
  initialTransactionId?: string;
  onVerificationComplete?: (result: any) => void;
  maxRetries?: number;
  retryDelay?: number;
  redirectPath?: string;
  redirectDelay?: number;
  disableAutoVerify?: boolean;
}

export const PaymentVerifierTailwind: React.FC<PaymentVerifierProps> = ({
  initialTransactionId,
  onVerificationComplete,
  maxRetries = 5,
  retryDelay = 1500,
  redirectPath = '/dashboard',
  redirectDelay = 1500,
  disableAutoVerify = false
}) => {
  const [transactionId, setTransactionId] = useState(initialTransactionId || '');
  const [isVerifying, setIsVerifying] = useState(!!initialTransactionId);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isAutoRetrying, setIsAutoRetrying] = useState(false);
  const [secondsToRedirect, setSecondsToRedirect] = useState<number | null>(null);
  const navigate = useNavigate();

  const { startVerification, endVerification, isVerifying: isGloballyVerifying } = useVerification();
  const autoDetectStarted = useRef<boolean>(false);

  // Función para normalizar IDs de transacción (con/sin prefijo 'trx')
  const normalizeTransactionId = useCallback((id: string) => {
    if (!id) return { raw: '', withPrefix: '', withoutPrefix: '' };
    
    const cleanId = id.trim();
    const hasPrefix = cleanId.startsWith('trx');
    
    return {
      raw: cleanId,
      withPrefix: hasPrefix ? cleanId : `trx${cleanId}`,
      withoutPrefix: hasPrefix ? cleanId.substring(3) : cleanId
    };
  }, []);

  // Función principal de verificación, ahora usando el contexto
  const verifyTransaction = useCallback(async (idToVerify: string) => {
    if (!startVerification(idToVerify)) {
      console.log(`[PaymentVerifier] Verificación para ${idToVerify} ya en curso (iniciada por otro componente/instancia). Omitiendo.`);
      setResult({ status: 'waiting', message: 'Verificación ya en progreso...'});
      return;
    }

    if (!idToVerify || idToVerify.trim() === '') {
      console.error('[PaymentVerifier] No se proporcionó ID de transacción válido para verificar.');
      endVerification(idToVerify);
      return;
    }

    const normalizedId = normalizeTransactionId(idToVerify);
    setTransactionId(normalizedId.raw);
    setIsVerifying(true);
    setError(null);
    const toastId = toast.loading('Verificando pago...', { duration: 5000 });

    try {
      console.log(`[PaymentVerifier] Verificando transacción: ${normalizedId.raw}`);
      const verificationResult = await PaykuValidator.processPayment(normalizedId.raw);
      console.log('[PaymentVerifier] Resultado de verificación:', verificationResult);
      setResult(verificationResult);

      if (verificationResult.success) {
        const alreadyProcessed = verificationResult.status === 'already_processed';
        const coinsAdded = verificationResult.coinsAdded || 0;
        if (alreadyProcessed) {
          toast.success(`Pago ya verificado anteriormente`, { id: toastId });
        } else {
          toast.success(`¡Pago verificado! ${coinsAdded > 0 ? `+${coinsAdded} monedas` : ''}`, { id: toastId });
          if (coinsAdded > 0) {
            toast.success(`¡${coinsAdded} monedas acreditadas a tu cuenta!`, {
              duration: 5000,
              icon: '💰',
              style: {
                borderRadius: '10px',
                background: '#10B981',
                color: '#fff',
                fontWeight: 'bold'
              },
            });
          }
        }
        sessionStorage.setItem('last_verification_result', JSON.stringify({
          txId: normalizedId.raw,
          success: true,
          timestamp: new Date().toISOString(),
          details: verificationResult.details
        }));
        localStorage.removeItem('pendingTransaction');
        sessionStorage.removeItem('pendingTransaction');
        window.dispatchEvent(new CustomEvent('coins-updated'));
      } else {
        const errorMsg = verificationResult.message || 'No se pudo verificar el pago';
        setError(errorMsg);
        toast.error(errorMsg, { id: toastId });
      }
      if (onVerificationComplete) {
        onVerificationComplete(verificationResult);
      }
    } catch (err) {
      console.error('[PaymentVerifier] Error al verificar pago:', err);
      const errorMsg = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMsg);
      toast.error('Error al verificar el pago', { id: toastId });
      setResult({ success: false, status: 'error', message: 'Error al procesar la verificación' });
      if (onVerificationComplete) {
        onVerificationComplete({ success: false, status: 'error', message: 'Error al procesar la verificación' });
      }
    } finally {
      setIsVerifying(false);
      endVerification(idToVerify);
    }
  }, [normalizeTransactionId, onVerificationComplete, startVerification, endVerification, setTransactionId, setIsVerifying, setError, setResult]);

  // Verificar automáticamente al cargar el componente
  useEffect(() => {
    const autoDetectAndVerify = async (id: string) => {
      // Extraer ID de transacción de múltiples fuentes
      const params = new URLSearchParams(window.location.search);
      const possibleParams = ['txid', 'payment_id', 'id', 'transaction_id'];
      
      let foundId = id;
      
      // 1. Buscar en parámetros de URL
      if (!foundId) {
        for (const param of possibleParams) {
          const value = params.get(param);
          if (value) {
            foundId = value;
            console.log(`ID encontrado en parámetro URL ${param}:`, foundId);
            break;
          }
        }
      }
      
      // 2. Buscar en storage
      if (!foundId) {
        const storageSources = [
          sessionStorage.getItem('last_payku_transaction'),
          sessionStorage.getItem('payku_transaction_to_verify'),
          localStorage.getItem('last_payku_transaction'),
          localStorage.getItem('payku_current_transaction'),
          sessionStorage.getItem('pendingTransaction'),
          localStorage.getItem('pendingTransaction')
        ];
        
        for (const source of storageSources) {
          if (source && source.trim() !== '') {
            try {
              if (source.startsWith('{')) {
                const parsed = JSON.parse(source);
                foundId = parsed.transaction_id || parsed.id || '';
              } else {
                foundId = source;
              }
              
              if (foundId) {
                console.log('ID encontrado en storage:', foundId);
                break;
              }
            } catch (e) {
              foundId = source;
              if (foundId) break;
            }
          }
        }
      }
      
      // Si encontramos un ID, verificar automáticamente
      if (foundId) {
        console.log('[PaymentVerifier] Verificando automáticamente ID:', foundId);
        setTransactionId(foundId);
        
        // Verificar si ya está en el registro de transacciones completadas
        if (typeof window !== 'undefined' && window.localStorage) {
          try {
            const completedTransactionsStr = window.localStorage.getItem('completed_transactions') || '[]';
            const completedTransactions = JSON.parse(completedTransactionsStr);
            
            if (completedTransactions.includes(foundId)) {
              console.log('Transacción ya completada según registro global, mostrando éxito sin volver a verificar');
              setResult({
                success: true,
                message: 'Transacción ya verificada y acreditada previamente',
                details: {
                  transaction: { transaction_id: foundId, status: 'success' },
                  source: 'global_completed_record',
                  coinsAdded: 0,
                  alreadyProcessed: true
                }
              });
              
              setIsVerifying(false);
              
              verifyTransaction(foundId);
              
              return;
            }
          } catch (e) {
            console.error('Error al verificar transacciones completadas:', e);
          }
        }
        
        verifyTransaction(foundId);
      } else {
        console.log('[PaymentVerifier] No se encontró ID de transacción para verificar automáticamente');
      }
    };
    
    // Solo ejecutar si no está deshabilitado Y si no se ha iniciado ya
    if (!disableAutoVerify) {
      if (autoDetectStarted.current) {
        console.log('[PaymentVerifier] autoDetectAndVerify ya iniciado, omitiendo ejecución duplicada.');
        return;
      }
      autoDetectStarted.current = true;
      console.log('[PaymentVerifier] Ejecutando autoDetectAndVerify...');
      autoDetectAndVerify(initialTransactionId || '');
    } else {
      console.log('[PaymentVerifier] Auto-verificación deshabilitada por prop.');
      if (initialTransactionId) {
        setTransactionId(initialTransactionId);
      }
    }
  }, [initialTransactionId, verifyTransaction, disableAutoVerify]);

  // Auto-retry para transacciones pendientes
  /* <---- INICIO COMENTARIO
  useEffect(() => {
    if (result && !result.success && retryCount < maxRetries && !isAutoRetrying) {
      setIsAutoRetrying(true);
      
      // Reducir el tiempo entre reintentos para una verificación más rápida
      const calculatedDelay = Math.min(retryDelay * Math.pow(1.1, retryCount), 2000);
      console.log(`Configurando auto-retry #${retryCount + 1} en ${calculatedDelay}ms`);
      
      toast.loading(`Verificando pago automáticamente... (${retryCount + 1}/${maxRetries})`, { 
        id: `retry-${retryCount}`,
        duration: calculatedDelay
      });
      
      const timerId = setTimeout(() => {
        setIsAutoRetrying(false);
        setRetryCount(count => count + 1);
        // Siempre forzar verificación en reintentos
        verifyTransaction(transactionId, true);
      }, calculatedDelay);
      
      return () => clearTimeout(timerId);
    }
  }, [result, retryCount, retryDelay, transactionId, maxRetries, isAutoRetrying]);
  */ // <---- FIN COMENTARIO
  
  // Redirección automática después de verificación exitosa
  useEffect(() => {
    if (result && result.success && redirectPath) {
      // Notificar inmediatamente la actualización de monedas
      const coinsAdded = result.details?.coinsAdded || result.details?.transaction?.vcoins_amount || 0;
      
      if (coinsAdded > 0) {
        // Mostrar toast destacado con las monedas acreditadas
        toast.success(
          `¡${coinsAdded} monedas acreditadas a tu cuenta!`, 
          { 
            duration: 5000,
            icon: '💰',
            style: {
              borderRadius: '10px',
              background: '#10B981',
              color: '#fff',
              fontWeight: 'bold'
            },
          }
        );
      }
      
      // Disparar evento de actualización inmediatamente
      window.dispatchEvent(new CustomEvent('coins-updated'));
      
      // Iniciar cuenta regresiva para redirección
      const totalSeconds = redirectDelay / 1000;
      setSecondsToRedirect(totalSeconds);
      
      const intervalId = setInterval(() => {
        setSecondsToRedirect(prev => {
          if (prev !== null && prev > 0) {
            return prev - 1;
          }
          return prev;
        });
      }, 1000);
      
      const timeoutId = setTimeout(() => {
        clearInterval(intervalId);
        // Disparar evento de actualización nuevamente antes de redirigir
        window.dispatchEvent(new CustomEvent('coins-updated'));
        navigate(redirectPath);
      }, redirectDelay);
      
      return () => {
        clearTimeout(timeoutId);
        clearInterval(intervalId);
      };
    }
  }, [result, redirectPath, redirectDelay, navigate]);

  // Renderizar interfaz según estado
  const renderStatus = () => {
    if (isVerifying) {
      return (
        <div className="flex flex-col items-center p-4">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-300">Verificando automáticamente el pago...</p>
        </div>
      );
    }
    
    if (isAutoRetrying) {
      return (
        <div className="flex flex-col items-center p-4 bg-blue-900/30 rounded-md">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-2"></div>
          <p className="text-lg font-bold text-blue-400">
            Verificación automática en proceso...
          </p>
          <p className="mt-2 text-center text-gray-300">
            Estamos verificando tu pago con Payku. Intento {retryCount + 1}/{maxRetries}
          </p>
        </div>
      );
    }

    if (result) {
      if (result.success) {
        // Mostrar información de monedas añadidas si está disponible
        const coinsAdded = result.details?.coinsAdded || result.details?.transaction?.vcoins_amount || 0;
        
        return (
          <div className="flex flex-col items-center p-4 bg-green-900/30 rounded-md">
            <CheckCircle className="w-10 h-10 text-green-500 mb-2" />
            <p className="text-lg font-bold text-green-400">
              ¡Pago verificado correctamente!
            </p>
            <p className="mt-2 text-gray-300">{result.message}</p>
            {coinsAdded > 0 && (
              <p className="mt-2 text-yellow-400 font-bold">
                +{coinsAdded} monedas añadidas a tu cuenta
              </p>
            )}
            {secondsToRedirect !== null && (
              <p className="mt-4 text-blue-300">
                Redireccionando al dashboard en {secondsToRedirect} {secondsToRedirect === 1 ? 'segundo' : 'segundos'}...
              </p>
            )}
          </div>
        );
      } else if (result.status === 'waiting') {
        return (
          <div className="mt-6 p-4 bg-indigo-900/20 border border-indigo-500/30 rounded-lg">
            <div className="flex items-center mb-3">
              <RefreshCw className="text-indigo-500 mr-2 h-5 w-5 animate-spin" />
              <h3 className="text-lg font-medium text-indigo-200">Verificación en progreso</h3>
            </div>
            <p className="text-sm text-indigo-100/80">
              Esta transacción ya está siendo verificada en otra ventana o proceso.
            </p>
            <p className="text-xs text-indigo-200/60 mt-2">
              Por favor, espera a que finalice la verificación actual.
            </p>
            
            <div className="mt-4 flex flex-col gap-2">
              <button 
                onClick={() => verifyTransaction(transactionId)} 
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded transition-colors flex items-center justify-center"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Forzar verificación
              </button>
              <button 
                onClick={() => {
                  endVerification(transactionId);
                  verifyTransaction(transactionId);
                }} 
                className="w-full bg-red-700 hover:bg-red-800 text-white font-medium py-2 px-4 rounded transition-colors flex items-center justify-center"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Desbloquear y verificar (emergencia)
              </button>
            </div>
          </div>
        );
      } else if (result.status === 'error' || 
                 result.status === 'cancelled' || 
                 result.status === 'failed' || 
                 result.status === 'expired' ||
                 result.status === 'pending' // También mostrar error claro si quedó pendiente
                ) {
        return (
          <div className="flex flex-col items-center p-4 bg-red-900/30 rounded-md border border-red-500/30">
            <XCircle className="w-10 h-10 text-red-500 mb-3" />
            <p className="text-lg font-bold text-red-400 mb-2">
              Error en Verificación
            </p>
            <p className="mt-1 text-center text-gray-300 text-sm">
              {result.message || 'No se pudo completar la verificación de tu pago.'}
            </p>
            
            {/* Añadir botones de acción */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3 w-full">
               <button
                  onClick={() => verifyTransaction(transactionId)} 
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <RefreshCw className="w-4 h-4" />
                  Reintentar Verificación
                </button>
               <button
                  onClick={() => navigate('/coins')} 
                  className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
                >
                 {/* Asumiendo que tienes un icono de monedas, si no, quitarlo o cambiarlo */}
                 {/* <Coins className="w-4 h-4" /> */}
                  Ir a Comprar Monedas
                </button>
            </div>
          </div>
        );
      }
    }

    // Estado inicial o sin resultado
    return (
      <div className="flex flex-col items-center p-4 bg-blue-900/30 rounded-md">
        <Clock className="w-10 h-10 text-blue-500 mb-2" />
        <p className="text-lg font-bold text-blue-400">
          Verificando tu pago
        </p>
        <p className="mt-2 text-center text-gray-300">
          Estamos procesando la verificación automática de tu pago. Espera un momento por favor.
        </p>
      </div>
    );
  };

  return (
    <div className="p-5 shadow-md w-full max-w-md bg-gray-800 rounded-lg border border-gray-700 mx-auto">
      <div className="flex flex-col gap-4">
        {renderStatus()}
      </div>
    </div>
  );
}; 